import { Download, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTaxExemptionFile, useTaxExemptionPage, type TaxExemptionDetail } from './api'
import { getApiErrorMessage } from '@/lib/api-error'

function Failed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-lg border bg-muted/40 text-sm text-muted-foreground" role="alert">
      {getApiErrorMessage(error, 'The file did not load.')}
      <Button onClick={onRetry} size="sm" variant="outline">
        Retry
      </Button>
    </div>
  )
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex aspect-[8.5/11] max-h-[78vh] w-full items-center justify-center gap-2 rounded-lg border bg-muted/40 text-sm text-muted-foreground" role="status">
      <Loader2 aria-hidden="true" className="size-4 animate-spin" />
      {label}
    </div>
  )
}

/** One PDF page, rendered by the API as an image (an iPhone downloads a PDF shown inside a page). */
function PdfPage({ form, page }: { form: TaxExemptionDetail; page: number }) {
  const image = useTaxExemptionPage(form.uuid, page)
  const label = form.page_count > 1 ? `Page ${page} of ${form.page_count}` : 'The uploaded form'
  if (image.error) return <Failed error={image.error} onRetry={() => void image.refetch()} />
  if (!image.url) return <Loading label={form.page_count > 1 ? `Loading page ${page}…` : 'Loading the form…'} />
  return <img alt={`${label}, ${form.original_file_name}`} className="w-full rounded-lg border bg-white" src={image.url} />
}

/** The form the customer uploaded, shown in place: a PDF as its pages' images, an image as itself. */
export function CertificatePreview({ form }: { form: TaxExemptionDetail }) {
  const file = useTaxExemptionFile(form.uuid)
  const pdf = form.content_type === 'application/pdf'
  return (
    <section aria-label="Uploaded form" className="min-w-0 space-y-2">
      <div className="flex min-w-0 items-center gap-1">
        <p className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground" title={form.original_file_name}>
          {form.original_file_name}
        </p>
        {file.url ? (
          <>
            <Button asChild className="h-7 px-2 text-xs" size="sm" variant="ghost">
              {/* eslint-disable-next-line no-restricted-syntax -- the uploaded file, already fetched with the staff token */}
              <a href={file.url} rel="noopener noreferrer" target="_blank">
                <ExternalLink aria-hidden="true" className="size-3.5" />
                New tab
              </a>
            </Button>
            <Button asChild className="h-7 px-2 text-xs" size="sm" variant="ghost">
              {/* eslint-disable-next-line no-restricted-syntax -- download the uploaded file */}
              <a download={form.original_file_name} href={file.url}>
                <Download aria-hidden="true" className="size-3.5" />
                Download
              </a>
            </Button>
          </>
        ) : null}
      </div>
      {pdf ? (
        form.page_count > 0 ? (
          <div className="grid gap-3">
            {Array.from({ length: form.page_count }, (_, index) => (
              <PdfPage form={form} key={index + 1} page={index + 1} />
            ))}
          </div>
        ) : (
          <p className="flex h-48 items-center justify-center rounded-lg border bg-muted/40 px-4 text-center text-sm text-muted-foreground">
            This PDF can't be shown here. Open it in a new tab or download it.
          </p>
        )
      ) : file.error ? (
        <Failed error={file.error} onRetry={() => void file.refetch()} />
      ) : !file.url ? (
        <Loading label="Loading the form…" />
      ) : (
        <div className="overflow-auto rounded-lg border bg-muted/40">
          <img alt={`The uploaded form, ${form.original_file_name}`} className="mx-auto max-h-[78vh] object-contain" src={file.url} />
        </div>
      )}
    </section>
  )
}
